import './styles/KeyboardStyle.css';
import { useState } from 'react';
import { HiOutlineBackspace } from 'react-icons/hi2';
import { BsShiftFill } from 'react-icons/bs';
import { BsShift } from 'react-icons/bs';
import { BsCapslockFill } from 'react-icons/bs';

interface KeyboardProps {
  onClickButton(option: string): void;
  keyBoardType?: string;
  disabled?: boolean;
  capsKeyboard?: boolean;
}

enum ButtonType {
  DEFAULT = 'default',
  SUCCESS = 'success',
  CANCEL = 'cancel',
  SPECIAL = 'special',
}
type Button = {
  content: any;
  buttonType: ButtonType;
  buttonSize?: number;
  action?(): void;
  doubleAction?(): void;
};
const Keyboard: React.FC<KeyboardProps> = ({
  onClickButton,
  keyBoardType,
  disabled,
  capsKeyboard,
}) => {
  const [shift, setShift] = useState(false);
  const [blockMayus, setBlockMayus] = useState(false);
  const [symbols, setSymbols] = useState(false);
  const sendLetter = (letter: string) => {
    if (shift || blockMayus || capsKeyboard) {
      onClickButton(letter.toLocaleUpperCase());
      if (shift) {
        setShift(false);
      }
      return;
    }
    onClickButton(letter);
  };
  const sendNumber = (number: string) => {
    onClickButton(number);
  };
  const renderLetter = (letter: string): string => {
    if (shift || blockMayus || capsKeyboard) {
      return letter.toLocaleUpperCase();
    }
    return letter;
  };
  const renderShift = () => {
    if (blockMayus) {
      return <BsCapslockFill />;
    }
    if (shift) {
      return <BsShiftFill />;
    }
    return <BsShift />;
  };
  const numbers = [
    {
      content: '1',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('1'),
    },
    {
      content: '2',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('2'),
    },
    {
      content: '3',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('3'),
    },
    {
      content: '4',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('4'),
    },
    {
      content: '5',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('5'),
    },
    {
      content: '6',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('6'),
    },
    {
      content: '7',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('7'),
    },
    {
      content: '8',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('8'),
    },
    {
      content: '9',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('9'),
    },
    {
      content: '0',
      buttonType: ButtonType.DEFAULT,
      action: () => sendNumber('0'),
    },
    {
      content: <HiOutlineBackspace />,
      action: () => onClickButton('back'),
      buttonType: ButtonType.CANCEL,
      buttonSize: 0.2,
    },
  ];
  const letters1 = [
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('q'),
      action: () => sendLetter('q'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('w'),
      action: () => sendLetter('w'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('e'),
      action: () => sendLetter('e'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('r'),
      action: () => sendLetter('r'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('t'),
      action: () => sendLetter('t'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('y'),
      action: () => sendLetter('y'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('u'),
      action: () => sendLetter('u'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('i'),
      action: () => sendLetter('i'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('o'),
      action: () => sendLetter('o'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('p'),
      action: () => sendLetter('p'),
    },
    {
      content: <HiOutlineBackspace />,
      action: () => onClickButton('back'),
      buttonType: ButtonType.CANCEL,
      buttonSize: 0.2,
    },
  ];
  const letters2 = [
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('a'),
      action: () => sendLetter('a'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('s'),
      action: () => sendLetter('s'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('d'),
      action: () => sendLetter('d'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('f'),
      action: () => sendLetter('f'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('g'),
      action: () => sendLetter('g'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('h'),
      action: () => sendLetter('h'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('j'),
      action: () => sendLetter('j'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('k'),
      action: () => sendLetter('k'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('l'),
      action: () => sendLetter('l'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('ñ'),
      action: () => sendLetter('ñ'),
    },
  ];
  const letters3 = [
    {
      buttonType: ButtonType.SPECIAL,
      content: renderShift(),
      action: () => {
        if (blockMayus) {
          setShift(false);
          setBlockMayus(false);
          return;
        }
        setShift(!shift);
      },
      doubleAction: () => setBlockMayus(true),
      buttonSize: 0.2,
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('z'),
      action: () => sendLetter('z'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('x'),
      action: () => sendLetter('x'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('c'),
      action: () => sendLetter('c'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('v'),
      action: () => sendLetter('v'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('b'),
      action: () => sendLetter('b'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('n'),
      action: () => sendLetter('n'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: renderLetter('m'),
      action: () => sendLetter('m'),
    },
  ];
  const symbols1 = [
    {
      buttonType: ButtonType.DEFAULT,
      content: '%',
      action: () => onClickButton('%'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '¡',
      action: () => onClickButton('¡'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '|',
      action: () => onClickButton('|'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '=',
      action: () => onClickButton('='),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '[',
      action: () => onClickButton('['),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: ']',
      action: () => onClickButton(']'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '<',
      action: () => onClickButton('<'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '>',
      action: () => onClickButton('>'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '{',
      action: () => onClickButton('{'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '}',
      action: () => onClickButton('}'),
    },
  ];
  const symbols2 = [
    {
      buttonType: ButtonType.DEFAULT,
      content: '@',
      action: () => onClickButton('@'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '#',
      action: () => onClickButton('#'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '$',
      action: () => onClickButton('$'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '_',
      action: () => onClickButton('_'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '&',
      action: () => onClickButton('&'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '-',
      action: () => onClickButton('-'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '+',
      action: () => onClickButton('+'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '(',
      action: () => onClickButton('('),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: ')',
      action: () => onClickButton(')'),
    },
  ];
  const symbols3 = [
    {
      buttonType: ButtonType.DEFAULT,
      content: '*',
      action: () => onClickButton('*'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '"',
      action: () => onClickButton('"'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: "'",
      action: () => onClickButton("'"),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: ':',
      action: () => onClickButton(':'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: ';',
      action: () => onClickButton(';'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '!',
      action: () => onClickButton('!'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '?',
      action: () => onClickButton('?'),
    },
    {
      buttonType: ButtonType.DEFAULT,
      content: '¿',
      action: () => onClickButton('¿'),
    },
  ];
  const defaultSpacer = [
    {
      content: '',
      buttonType: ButtonType.DEFAULT,
      buttonSize: 0.5,
      action: () => onClickButton(' '),
    },
  ];
  const symbolSpacer = [
    {
      content: symbols ? 'ABC' : '?@+=',
      buttonType: ButtonType.SPECIAL,
      buttonSize: 0.2,
      action: () => setSymbols(!symbols),
    },
    {
      content: ',',
      buttonType: ButtonType.DEFAULT,
      action: () => onClickButton(','),
    },
    {
      content: '',
      buttonType: ButtonType.DEFAULT,
      buttonSize: 0.3,
      action: () => onClickButton(' '),
    },
    {
      content: '.',
      buttonType: ButtonType.DEFAULT,
      action: () => onClickButton('.'),
    },
  ];
  const completeSymbolKeyboard: Button[][] = [
    numbers,
    symbols1,
    symbols2,
    symbols3,
    symbolSpacer,
  ];
  const completeKeyboard: Button[][] = [
    numbers,
    letters1.slice(0, -1),
    letters2,
    letters3,
    symbolSpacer,
  ];
  const basicKeyboard: Button[][] = [
    letters1,
    letters2,
    letters3,
    defaultSpacer,
  ];

  const chooseKeyboard = () => {
    if (keyBoardType === 'password') {
      if (symbols) {
        return completeSymbolKeyboard;
      }
      return completeKeyboard;
    }
    return basicKeyboard;
  };
  return (
    <div className="keyboard-container">
      {chooseKeyboard().map((element) => {
        return (
          <div className="keyboard-row">
            {element.map((button: Button) => {
              if (button.buttonType !== ButtonType.SPECIAL || keyBoardType) {
                return (
                  <button
                    disabled={disabled}
                    onClick={() => {
                      if (
                        button.action &&
                        typeof button.action === 'function'
                      ) {
                        button.action();
                      }
                    }}
                    onDoubleClick={() => {
                      if (
                        button.doubleAction &&
                        typeof button.doubleAction === 'function'
                      ) {
                        button.doubleAction();
                      }
                    }}
                    style={{ flex: button.buttonSize ?? 0.1 }}
                    className={`keyboard-button ${button.buttonType} stepOn`}
                  >
                    {button.content}
                  </button>
                );
              }
              return null;
            })}
          </div>
        );
      })}
    </div>
  );
};

export { Keyboard };
